import React, { useCallback, useEffect, useRef } from 'react'
import { ControlledMenu, MenuItem, useMenuState } from '@szhsin/react-menu'
import { useTranslation } from 'react-i18next'
import Checkbox from '@src/components/Checkbox'

import useOrgStructureStore, { BulkSelect } from '@pages/Team/Members/List/store'
import { IconCheck } from '@tabler/icons-react'

import styles from './index.module.css'

const SelectHeader = () => {
  const { t } = useTranslation('org')
  const selectRecords = useOrgStructureStore.use.selectRecords()
  const removeSelection = useOrgStructureStore.use.removeSelection()
  const filter = useOrgStructureStore.use.filter()
  const count = useOrgStructureStore.use.count()
  const records = useOrgStructureStore.use.records()
  const selected = useOrgStructureStore.use.bulkSelect()
  const checkboxRef = useRef<HTMLInputElement>(null)
  const [{ state: menuState }, toggleMenu] = useMenuState()

  const limit = filter.limit || 30

  const pageSize = count > limit ? limit : count
  const allAreOnPage = pageSize < limit

  const selectOptions: Option<BulkSelect>[] = [
    { value: BulkSelect.page, label: t('selectOnPage', { value: pageSize }) },
    { value: BulkSelect.all, label: t('selectAll', { value: count }) },
  ]

  const handleSelect = (selectedOption: Option<BulkSelect>) => {
    toggleMenu()
    if (selectedOption.value !== selected) {
      selectRecords(selectedOption.value, true)
    }
  }

  const handleCheckboxClick = useCallback(() => {
    if (selected) {
      return removeSelection()
    }
    if (allAreOnPage) {
      selectRecords(BulkSelect.page, true)
    } else {
      toggleMenu()
    }
  }, [toggleMenu, removeSelection, selected, allAreOnPage])

  useEffect(() => {
    if (menuState === 'open') {
      toggleMenu()
    }
  }, [records])

  return (
    <div>
      {/* No chevron. ``handleCheckboxClick`` above already opens the
          "Select 30 on page / Select all 124 in the table" dropdown
          when there are multiple pages — so the chevron was a
          duplicate trigger that read visually as a sort indicator and
          shifted the checkbox off the column's center. With the
          chevron gone, the header renders the exact same ``Checkbox``
          element as every per-row ``SelectCell``, in cells with the
          identical ``first:!pl-3 first:!w-12`` geometry set on the
          Team table — header and row checkboxes land at the same X. */}
      <Checkbox
        ref={checkboxRef}
        checked={!!selected}
        onChange={handleCheckboxClick}
        disabled={!count}
        className="flex items-center justify-center p-0 m-0"
      />

      <ControlledMenu
        menuClassName="!-top-1 !left-6 rounded-base text-xs "
        state={menuState}
        anchorRef={checkboxRef}
        className={styles.selectHeaderContainer}
      >
        {selectOptions.map((option) => {
          return (
            <MenuItem
              className="px-4 py-0.5 m-0 bg-transparent hover:bg-blue-50 normal-case font-normal relative"
              onClick={() => handleSelect(option)}
              key={option.value}
            >
              {selected === option.value && <IconCheck className="w-3 h-3 absolute left-[3px] text-brand_primary" />}
              {option.label}
            </MenuItem>
          )
        })}
      </ControlledMenu>
    </div>
  )
}

export default SelectHeader
